import { zodResolver } from "@hookform/resolvers/zod";
import { Button, IconButton } from "@hortis/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@hortis/ui/dialog";
import styles from "@hortis/ui/dialog/dialog.module.css";
import { FormControl, FormField, FormItem, FormMessage } from "@hortis/ui/form";
import { ClockFastForward, ImagePlus, Trash3, XClose } from "@hortis/ui/icons";
import { RichTextEditor } from "@hortis/ui/rich-text-editor";
import { toast } from "@hortis/ui/sonner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@hortis/ui/tooltip";
import { useParams, useRouteContext } from "@tanstack/react-router";
import { ObservationConditionType, namedOperations } from "generated/graphql";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { queryClient } from "src/app";
import { getFollowUpLabel } from "src/components/modals/make-observation/follow-up-options";
import { followUpSchema } from "src/components/modals/make-observation/schema";
import { FollowUpPicker } from "src/components/pickers/follow-up-picker/follow-up-picker";
import { ObservationConditionPicker } from "src/components/pickers/observation-condition-picker/observation-condition-picker";
import { mention } from "src/components/rich-text-editor/mention";
import { stopEventPropagation } from "src/features/collection/components/stop-event-propagation";
import {
  AddMaterialPhotosButton,
  StagedImage,
} from "src/features/create-accession/components/accession-data-form/add-material-photos-button";
import { DiscardDialog } from "src/features/create-accession/components/accession-data-form/discard-dialog";
import { MaterialPhotos } from "src/features/create-accession/components/accession-data-form/material-photos";
import { RemoveDialog } from "src/features/create-accession/components/accession-data-form/remove-dialog";
import { UploadsInProgressDialog } from "src/features/create-accession/components/accession-data-form/uploads-in-progress-dialog";
import { MaterialImageSchema } from "src/features/create-accession/components/add-plant-material-form/material-image-schema";
import { convertDatesToISO } from "src/utils/convert-dates-to-iso";
import { useGqlMutation } from "src/utils/hooks/graphql";
import { observationConditionLabelMap } from "src/utils/label-maps/observation-condition";
import { tiptapSchema } from "src/utils/tiptap";
import { useAccessTokenBasic } from "src/utils/use-access-token";
import { twMerge } from "tailwind-merge";
import * as uuid from "uuid";
import { z } from "zod";
import { ObservationConditionRating } from "../sections/material-record-observations/observation-condition";
import { createObservationDocument } from "./graphql";

export const createObservationSchema = z.object({
  condition: z.nativeEnum(ObservationConditionType),
  bodyData: tiptapSchema.nullish(),
  images: z.array(MaterialImageSchema).nullish(),
  followUp: followUpSchema.nullish(),
});

export const CreateObservationDialog = ({
  open: openControlled,
  onOpenChange: onOpenChangeControlled,
  materialId,
  children,
  onSubmit,
  initialValues,
  onSuccess,
  showRemove = false,
  siteSlug: siteSlugOverride,
}: {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  initialValues?: Partial<z.infer<typeof createObservationSchema>>;
  materialId?: string;
  children?: ReactNode;
  onSubmit?: (data: z.infer<typeof createObservationSchema> | null) => void;
  onSuccess?: () => void;
  showRemove?: boolean;
  siteSlug?: string;
}) => {
  const defaultStagedPhotos = () =>
    initialValues?.images?.map((props) => ({
      ...props,
      id: uuid.v4(),
      thumb: props.assetUrl,
      complete: true,
    })) ?? [];
  const defaultValues = () => ({
    ...initialValues,
    condition: initialValues?.condition ?? ObservationConditionType.Good,
  });

  const [openInternal, onOpenChangeInternal] = useState(
    openControlled ?? false,
  );
  const open = openControlled ?? openInternal;
  const onOpenChange = onOpenChangeControlled ?? onOpenChangeInternal;
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const accessToken = useAccessTokenBasic();
  const { subdomain } = useRouteContext({ from: "__root__" });
  const { siteSlug } = useParams({ strict: false });
  const [stagedPhotos, setStagedPhotos] =
    useState<Array<StagedImage>>(defaultStagedPhotos);
  const [uploadsInProgressDialogOpen, setUploadsInProgressDialogOpen] =
    useState(false);

  const form = useForm<z.infer<typeof createObservationSchema>>({
    defaultValues: initialValues,
    criteriaMode: "all",
    resolver: zodResolver(createObservationSchema),
  });

  const createObservation = useGqlMutation({
    mutation: createObservationDocument,
    onError: () => {
      toast.error("Failed to create observation");
    },
    onSuccess: (res) => {
      if (!res.createObservation.success) {
        toast.error("Failed to create observation");
      }
      toast.success("Observation added");
      void queryClient.invalidateQueries({
        queryKey: [namedOperations.Query.GetDashboard],
      });
      void queryClient.invalidateQueries({
        queryKey: [namedOperations.Query.GetMaterial],
      });
      void queryClient.invalidateQueries({
        queryKey: [namedOperations.Query.PlantMaterials],
      });
      onOpenChange(false);
      onSuccess?.();
      form.reset(defaultValues);
      setStagedPhotos(defaultStagedPhotos);
    },
  });

  const handleOpenChange = (open: boolean) => {
    if (
      (Object.keys(form.formState.dirtyFields).length !== 0 ||
        stagedPhotos.some((i) => !i.complete)) &&
      !discardDialogOpen
    ) {
      setDiscardDialogOpen(true);
      return;
    }
    onOpenChange(open);
  };

  const handleSubmit = async (
    data: z.infer<typeof createObservationSchema>,
  ) => {
    if (!uploadsInProgressDialogOpen && stagedPhotos.some((i) => !i.complete)) {
      setUploadsInProgressDialogOpen(true);
      return;
    }
    const { followUp, ...rest } = data;
    const dataWithImages = {
      ...rest,
      images: stagedPhotos
        .filter((i): i is typeof i & { assetUrl: string } => i.assetUrl != null)
        .filter((i) => i.complete)
        .map(({ assetUrl, description, capturedAt, capturedBy }) => ({
          assetUrl,
          description,
          capturedBy,
          capturedAt,
        })),
    };

    if (onSubmit != null) {
      onSubmit({ ...dataWithImages, followUp });
      onOpenChange(false);
      return;
    }

    if (materialId == null) {
      toast.error("Failed to create observation");
      throw new Error("Material ID is required");
    }

    await createObservation.mutateAsync({
      followUp,
      input: {
        ...dataWithImages,
        materialId,
        images: dataWithImages.images.map(convertDatesToISO),
      },
    });
  };

  useEffect(() => {
    if (open) {
      form.reset(defaultValues());
      setStagedPhotos(defaultStagedPhotos);
    }
    setDiscardDialogOpen(false);
  }, [open, form]);

  const content = (
    <FormProvider {...form}>
      <UploadsInProgressDialog
        open={uploadsInProgressDialogOpen}
        onOpenChange={setUploadsInProgressDialogOpen}
        onDiscard={() => {
          form.handleSubmit(handleSubmit)();
        }}
      />
      <div className="absolute right-5 top-5 gap-1 flex items-center">
        {showRemove && initialValues != null && (
          <Tooltip>
            <RemoveDialog
              onDiscard={() => {
                onOpenChange(false);
                if (onSubmit != null) {
                  onSubmit(null);
                }
              }}
              section="observation"
            >
              <DialogTrigger asChild onFocusCapture={stopEventPropagation}>
                <TooltipTrigger asChild>
                  <IconButton
                    icon={Trash3}
                    size="sm"
                    className="p-1.5"
                    ariaLabel="Remove observation"
                    variant="tertiaryGray"
                    testId="remove-observation-button"
                  />
                </TooltipTrigger>
              </DialogTrigger>
            </RemoveDialog>
            <TooltipContent side="bottom" sideOffset={0}>
              Remove observation
            </TooltipContent>
          </Tooltip>
        )}
        <Tooltip>
          <DiscardDialog
            onDiscard={() => {
              handleOpenChange(false);
            }}
            open={discardDialogOpen}
            onOpenChange={setDiscardDialogOpen}
          >
            <TooltipTrigger asChild>
              <IconButton
                ariaLabel="Close"
                variant="tertiaryGray"
                testId="close-create-observation-dialog"
                className="p-1.5"
                size="sm"
                icon={XClose}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenChange(false);
                }}
                onFocusCapture={stopEventPropagation}
              />
            </TooltipTrigger>
          </DiscardDialog>
          <TooltipContent side="bottom" sideOffset={0}>
            Discard observation
          </TooltipContent>
        </Tooltip>
      </div>
      <form>
        <div className="space-y-4 px-5 pb-4 pt-5">
          <FormField
            control={form.control}
            name="condition"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ObservationConditionPicker
                    value={field.value}
                    onChange={field.onChange}
                  >
                    <Button size="xs" className="w-fit">
                      <ObservationConditionRating condition={field.value} />
                      {observationConditionLabelMap[field.value]}
                    </Button>
                  </ObservationConditionPicker>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="bodyData"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  {accessToken != null && (
                    <RichTextEditor
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Add comments..."
                      data-cy="observation-body-input"
                      extensions={[
                        mention({
                          accessToken,
                          siteSlug: siteSlug ?? siteSlugOverride,
                          subdomain,
                        }),
                      ]}
                      className="rounded-none border-none shadow-none focus-within:ring-0 [&_.richTextEditor]:min-h-16 [&_.richTextEditor]:px-0 [&_.richTextEditor]:py-0"
                    />
                  )}
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <MaterialPhotos
            images={stagedPhotos}
            deletePhoto={(id) => {
              setStagedPhotos((images) => images.filter((i) => i.id !== id));
            }}
            onPhotoSave={(id, newPhoto) => {
              setStagedPhotos((images) =>
                images.map((image) => (image.id === id ? newPhoto : image)),
              );
            }}
          />
        </div>
        <div className="flex items-center justify-between gap-3 border-t border-grey-100 py-3 pl-4 pr-5">
          <div className="flex items-center gap-2">
            <FormField
              control={form.control}
              name="followUp"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Tooltip>
                      <FollowUpPicker
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- no form typesafety yet
                        value={field.value}
                        onChange={field.onChange}
                      >
                        <TooltipTrigger asChild>
                          <Button
                            variant="tertiaryGray"
                            size="sm"
                            startIcon={ClockFastForward}
                            className="px-2"
                            onFocusCapture={stopEventPropagation}
                          >
                            {field.value != null && (
                              <div className="flex items-center gap-2 text-grey-500">
                                {getFollowUpLabel(field.value)?.label}
                              </div>
                            )}
                          </Button>
                        </TooltipTrigger>
                      </FollowUpPicker>
                      <TooltipContent side="bottom" sideOffset={0}>
                        {field.value == null
                          ? "Add follow-up"
                          : "Change follow-up"}
                      </TooltipContent>
                    </Tooltip>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <AddMaterialPhotosButton
              images={stagedPhotos}
              setImages={setStagedPhotos}
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <IconButton
                    icon={ImagePlus}
                    size="sm"
                    variant="tertiaryGray"
                    ariaLabel="Add photos"
                    testId="add-material-photo-button"
                    onFocusCapture={stopEventPropagation}
                    tabIndex={-1}
                  />
                </TooltipTrigger>
                <TooltipContent side="bottom" sideOffset={0}>
                  Add photos
                </TooltipContent>
              </Tooltip>
            </AddMaterialPhotosButton>
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="primary"
                type="submit"
                size="sm"
                loading={form.formState.isSubmitting}
                testId="create-observation-button"
                onFocusCapture={stopEventPropagation}
                disabled={!open}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  form.handleSubmit(handleSubmit)(e);
                }}
              >
                Add observation
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" sideOffset={0}>
              Add observation
            </TooltipContent>
          </Tooltip>
        </div>
      </form>
    </FormProvider>
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {children}
      <DialogContent
        className={twMerge(
          "top-12 flex max-h-[calc(100%-96px)] w-full max-w-[calc(100%-32px)] md:max-w-[550px] translate-y-0 flex-col gap-0! rounded-xl bg-white p-0! [@media(min-height:953px)]:top-32 [@media(min-height:953px)]:max-h-[calc(100%-256px)]",
          styles.dialogNoY,
        )}
        data-cy="create-observation-dialog"
      >
        <DialogTitle className="sr-only">Add observation</DialogTitle>
        <DialogDescription className="sr-only">
          Add observation form
        </DialogDescription>
        {content}
      </DialogContent>
    </Dialog>
  );
};
